import { createState, State, useHookstate, useState } from '@hookstate/core';
import { NavigationContext } from '@layout/navigation/navigation';
import { IIdentificationRequest } from '@models/IdentificationRequests.model';
import { getIdentificationRequests } from '@services/queries/identificationRequests/getIdentificationRequests';
import {
  getIdentificationRequestsCount,
  IGetIdentificationRequestsCount,
} from '@services/queries/onboarding/getIdentificationRequestsCount';
import { createContext, useContext, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { filterState } from './components/Filter/components/FilterItem/FilterItem';

import { IdentificationRequestsRaw } from './IdentificationRequests.raw';

const useInfiniteQueryParams = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

interface IIdentificationRequestsContext {
  status: string | null;
  assignedToMe: State<boolean>;
  identificationRequestsCount?: IGetIdentificationRequestsCount;
  openChat: State<boolean>;
  selectedRequest: State<IIdentificationRequest | null>;
}

export const IdentificationRequestsContext =
  createContext<IIdentificationRequestsContext>({
    status: 'open',
    assignedToMe: createState<boolean>(false),
    openChat: createState<boolean>(false),
    selectedRequest: createState<IIdentificationRequest | null>(null),
  });

const IdentificationRequestsQuery = () => {
  const history = useHistory();

  const query = useInfiniteQueryParams();

  const { userId } = useContext(NavigationContext);

  const assignedToMe = useState(false);

  const openChat = useState<boolean>(false);

  const selectedRequest = useState<IIdentificationRequest | null>(null);

  const status = query.get('tab');

  const studiosFilter = useHookstate(filterState);

  if (!status || !['open', 'resolved', 'closed'].includes(status)) {
    history.push(`/identificationRequests?tab=open`);
  }

  const { data: identificationRequestsCount } = useQuery(
    ['identificationRequests', 'count'],
    getIdentificationRequestsCount,
  );

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery(
    [
      'identificationRequests',
      'assets',
      status,
      assignedToMe.value,
      studiosFilter.get(),
    ],
    async () => {
      return getIdentificationRequests({
        status,
        assignedId: assignedToMe.value ? userId : undefined,
        studioId: studiosFilter.get(),
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        !lastPage.isLastPage ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  return (
    <IdentificationRequestsContext.Provider
      value={{
        status,
        identificationRequestsCount,
        assignedToMe,
        openChat,
        selectedRequest,
      }}
    >
      <IdentificationRequestsRaw
        filesQuery={data}
        fetchNextPage={fetchNextPage}
        isError={isError}
        isLoading={isLoading}
      />
    </IdentificationRequestsContext.Provider>
  );
};

export { IdentificationRequestsQuery };
